import React, { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Separator } from "./ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import { Alert, AlertDescription } from "./ui/alert";
import { 
  TrendingUp, 
  TrendingDown, 
  AlertTriangle, 
  CheckCircle, 
  Activity, 
  Target,
  Calendar,
  DollarSign,
  Percent,
  FileBarChart,
  Settings
} from "lucide-react";
import RateTrendsChart from "./RateTrendsChart";
import VarianceAlerts from "./VarianceAlerts";
import BaselineManagement from "./BaselineManagement";
import axios from "axios";

const API = require('../services/apiBase').default || require('../services/apiBase');

const VarianceMonitoring = ({ calculationId, filename }) => {
  const [varianceData, setVarianceData] = useState(null);
  const [historicalRates, setHistoricalRates] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [baseline, setBaseline] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (calculationId) {
      loadVarianceData();
    }
  }, [calculationId]);

  const loadVarianceData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Load all variance monitoring data in parallel
      const [varianceResponse, historicalResponse, alertsResponse, baselineResponse] = await Promise.all([
        axios.get(`${API}/variance-analysis/${calculationId}`),
        axios.get(`${API}/historical-rates`),
        axios.get(`${API}/variance-alerts`, { params: { calculation_id: calculationId } }),
        axios.get(`${API}/baseline-rates`, { params: { calculation_id: calculationId } })
      ]);

      setVarianceData(varianceResponse.data);
      setHistoricalRates(historicalResponse.data.historical_rates || []);
      setAlerts(alertsResponse.data.alerts || []);
      setBaseline(baselineResponse.data.has_baseline ? baselineResponse.data.baseline : null);
      
    } catch (e) {
      setError(e.response?.data?.detail || "Error loading variance monitoring data");
    } finally {
      setLoading(false);
    }
  };

  const handleBaselineUpdate = () => {
    // Refresh data when baseline is updated
    loadVarianceData();
  };

  const formatPercentage = (value) => {
    return value || value === 0 ? `${Number(value).toFixed(2)}%` : 'N/A';
  };

  const formatCurrency = (value) => {
    return value || value === 0 ? `$${Number(value).toLocaleString()}` : 'N/A';
  };

  const getVarianceColor = (variance, isPercentage = false) => {
    if (variance === undefined || variance === null) return 'text-gray-400';
    const absVariance = Math.abs(variance);
    if (isPercentage) {
      if (absVariance < 2) return 'text-green-600';
      if (absVariance < 5) return 'text-yellow-600';
      if (absVariance < 10) return 'text-orange-600';
      return 'text-red-600';
    } else {
      if (absVariance < 5000) return 'text-green-600';
      if (absVariance < 10000) return 'text-yellow-600';
      if (absVariance < 25000) return 'text-orange-600';
      return 'text-red-600';
    }
  };

  const getVarianceIcon = (variance, threshold) => {
    if (variance === undefined || variance === null) return <Activity className="h-4 w-4 text-gray-400" />;
    const absVariance = Math.abs(variance);
    if (absVariance > threshold) {
      return variance > 0 ? 
        <TrendingUp className="h-4 w-4 text-red-600" /> : 
        <TrendingDown className="h-4 w-4 text-red-600" />;
    }
    return <CheckCircle className="h-4 w-4 text-green-600" />;
  };

  const renderVarianceSummary = () => {
    if (!varianceData || !varianceData.baseline_comparison || !baseline) {
      return (
        <Alert className="border-yellow-200 bg-yellow-50">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription className="text-yellow-800">
            No baseline rates set. Set baseline rates to enable variance monitoring.
          </AlertDescription>
        </Alert>
      );
    }

    const { baseline_comparison } = varianceData;

    return (
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* G&amp;A Rate Variance */}
        {baseline_comparison.ga_rate && (
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-2">
                  <Target className="h-5 w-5 text-blue-600" />
                  <h3 className="font-semibold text-blue-900">G&amp;A Rate</h3>
                </div>
                {getVarianceIcon(baseline_comparison.ga_rate.variance_percent, 5)}
              </div>
              
              <div className="space-y-3">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600">Current:</span>
                  <span className="font-medium">{formatPercentage(baseline_comparison.ga_rate.current)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600">Baseline:</span>
                  <span className="font-medium">{formatPercentage(baseline_comparison.ga_rate.baseline)}</span>
                </div>
                <Separator />
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600">Variance:</span>
                  <span className={`font-bold ${getVarianceColor(baseline_comparison.ga_rate.variance_percent, true)}`}>
                    {baseline_comparison.ga_rate.variance_percent > 0 ? '+' : ''}
                    {formatPercentage(baseline_comparison.ga_rate.variance_percent)}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600">Absolute:</span>
                  <span className={`font-medium ${getVarianceColor(baseline_comparison.ga_rate.variance_absolute, true)}`}>
                    {baseline_comparison.ga_rate.variance_absolute > 0 ? '+' : ''}
                    {formatPercentage(baseline_comparison.ga_rate.variance_absolute)}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Overhead Rate Variance */}
        {baseline_comparison.overhead_rate && (
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-2">
                  <DollarSign className="h-5 w-5 text-blue-600" />
                  <h3 className="font-semibold text-blue-900">Overhead Rate</h3>
                </div>
                {getVarianceIcon(baseline_comparison.overhead_rate.variance_percent, 5)}
              </div>
              
              <div className="space-y-3">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600">Current:</span>
                  <span className="font-medium">{formatPercentage(baseline_comparison.overhead_rate.current)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600">Baseline:</span>
                  <span className="font-medium">{formatPercentage(baseline_comparison.overhead_rate.baseline)}</span>
                </div>
                <Separator />
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600">Variance:</span>
                  <span className={`font-bold ${getVarianceColor(baseline_comparison.overhead_rate.variance_percent, true)}`}>
                    {baseline_comparison.overhead_rate.variance_percent > 0 ? '+' : ''}
                    {formatPercentage(baseline_comparison.overhead_rate.variance_percent)}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600">Absolute:</span>
                  <span className={`font-medium ${getVarianceColor(baseline_comparison.overhead_rate.variance_absolute, true)}`}>
                    {baseline_comparison.overhead_rate.variance_absolute > 0 ? '+' : ''}
                    {formatPercentage(baseline_comparison.overhead_rate.variance_absolute)}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Fringe Rate Variance */}
        {baseline_comparison.fringe_rate && (
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-2">
                  <Percent className="h-5 w-5 text-green-600" />
                  <h3 className="font-semibold text-green-900">Fringe Rate</h3>
                </div>
                {getVarianceIcon(baseline_comparison.fringe_rate.variance_percent, 5)}
              </div>
              
              <div className="space-y-3">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600">Current:</span>
                  <span className="font-medium">{formatPercentage(baseline_comparison.fringe_rate.current)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600">Baseline:</span>
                  <span className="font-medium">{formatPercentage(baseline_comparison.fringe_rate.baseline)}</span>
                </div>
                <Separator />
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600">Variance:</span>
                  <span className={`font-bold ${getVarianceColor(baseline_comparison.fringe_rate.variance_percent, true)}`}>
                    {baseline_comparison.fringe_rate.variance_percent > 0 ? '+' : ''}
                    {formatPercentage(baseline_comparison.fringe_rate.variance_percent)}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600">Absolute:</span>
                  <span className={`font-medium ${getVarianceColor(baseline_comparison.fringe_rate.variance_absolute, true)}`}>
                    {baseline_comparison.fringe_rate.variance_absolute > 0 ? '+' : ''}
                    {formatPercentage(baseline_comparison.fringe_rate.variance_absolute)}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    );
  };

  const renderVarianceStats = () => {
    if (!varianceData || !varianceData.variance_summary) return null;

    const { variance_summary } = varianceData;

    return (
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center space-x-2">
              <AlertTriangle className="h-5 w-5 text-orange-600" />
              <div>
                <p className="text-sm font-medium text-orange-900">Total Alerts</p>
                <p className="text-2xl font-bold text-orange-600">{variance_summary.total_alerts || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center space-x-2">
              <TrendingUp className="h-5 w-5 text-red-600" />
              <div>
                <p className="text-sm font-medium text-red-900">Critical</p>
                <p className="text-2xl font-bold text-red-600">{variance_summary.critical_alerts || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center space-x-2">
              <Activity className="h-5 w-5 text-yellow-600" />
              <div>
                <p className="text-sm font-medium text-yellow-900">Warnings</p>
                <p className="text-2xl font-bold text-yellow-600">{variance_summary.warning_alerts || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center space-x-2">
              <CheckCircle className="h-5 w-5 text-green-600" />
              <div>
                <p className="text-sm font-medium text-green-900">Baseline Set</p>
                <p className="text-sm font-bold text-green-600">
                  {variance_summary.has_baseline ? "Yes" : "No"}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  };

  if (loading) {
    return (
      <Card className="shadow-lg">
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <FileBarChart className="h-5 w-5" />
            <span>Variance Monitoring</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <span className="ml-2">Loading variance monitoring data...</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="shadow-lg">
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <FileBarChart className="h-5 w-5" />
            <span>Variance Monitoring</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Alert className="border-red-200 bg-red-50">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription className="text-red-700">
              {error}
            </AlertDescription>
          </Alert>
          <Button onClick={loadVarianceData} className="mt-4">
            Retry
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="shadow-lg">
      <CardHeader>
        <CardTitle className="flex items-center space-x-2">
          <FileBarChart className="h-5 w-5 text-blue-600" />
          <span>Variance Monitoring</span>
          <Badge variant="secondary">{filename}</Badge>
        </CardTitle>
      </CardHeader>
      
      <CardContent>
        <Tabs defaultValue="dashboard" className="w-full">
          <TabsList className="grid w-full grid-cols-4 mb-6">
            <TabsTrigger value="dashboard" className="flex items-center space-x-2">
              <FileBarChart className="h-4 w-4" />
              <span>Dashboard</span>
            </TabsTrigger>
            <TabsTrigger value="trends" className="flex items-center space-x-2">
              <TrendingUp className="h-4 w-4" />
              <span>Rate Trends</span>
            </TabsTrigger>
            <TabsTrigger value="alerts" className="flex items-center space-x-2">
              <AlertTriangle className="h-4 w-4" />
              <span>Alerts</span>
            </TabsTrigger>
            <TabsTrigger value="settings" className="flex items-center space-x-2">
              <Settings className="h-4 w-4" />
              <span>Baseline &amp; Settings</span>
            </TabsTrigger>
          </TabsList>

          <TabsContent value="dashboard" className="space-y-6">
            {renderVarianceStats()}
            
            <div>
              <h3 className="text-lg font-semibold mb-4 flex items-center space-x-2">
                <Target className="h-5 w-5" />
                <span>Current vs Baseline Comparison</span>
              </h3>
              {renderVarianceSummary()}
            </div>
          </TabsContent>

          <TabsContent value="trends">
            <RateTrendsChart 
              historicalRates={historicalRates}
              baseline={baseline}
              onRefresh={loadVarianceData}
            />
          </TabsContent>

          <TabsContent value="alerts">
            <VarianceAlerts 
              alerts={alerts}
              onRefresh={loadVarianceData}
            />
          </TabsContent>

          <TabsContent value="settings">
            <BaselineManagement 
              calculationId={calculationId}
              currentBaseline={baseline}
              onBaselineUpdate={handleBaselineUpdate}
            />
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
};

export default VarianceMonitoring;